/**
 * Delivery Helper
 * Calculates delivery time, fees, and handling charges based on cart items and order time
 * Matches ForFeederAndroid's DeliveryHelper logic
 */

const CUTOFF_HOUR = 16; // 4 PM
const DELIVERY_FEE_STANDARD = 40;
const HANDLING_FEE_STANDARD = 10;
const MIN_ORDER_FOR_FREE_DELIVERY = 100;

export const DeliveryType = {
  SAME_DAY: 'SAME_DAY',
  NEXT_DAY_LOOSE_ITEMS: 'NEXT_DAY_LOOSE_ITEMS',
  NEXT_DAY_AFTER_CUTOFF: 'NEXT_DAY_AFTER_CUTOFF'
};

/**
 * Calculate comprehensive delivery information
 * @param {Array} cartItems - Array of cart items
 * @param {number} subtotal - Subtotal amount
 * @returns {Object} Delivery information including fees, time, and type
 */
export const calculateDeliveryInfo = (cartItems, subtotal) => {
  const hasLooseItems = cartItems.some(item => item.loose_item === true);
  const currentHour = new Date().getHours();
  const isBeforeCutoff = currentHour < CUTOFF_HOUR;

  // Scenario 1: Has loose items - always next day delivery with free fees
  if (hasLooseItems) {
    return {
      hasLooseItems: true,
      actualDeliveryFee: 0,
      actualHandlingFee: 0,
      originalDeliveryFee: DELIVERY_FEE_STANDARD,
      originalHandlingFee: HANDLING_FEE_STANDARD,
      estimatedTime: getNextDayDeliveryTime(),
      deliveryType: DeliveryType.NEXT_DAY_LOOSE_ITEMS,
      timeRemaining: isBeforeCutoff ? getTimeRemainingUntilCutoff() : '',
      canDeliverToday: false
    };
  }

  // Scenario 2a: No loose items, before 4 PM, cart >= 100 - same day with free fees
  if (isBeforeCutoff && subtotal >= MIN_ORDER_FOR_FREE_DELIVERY) {
    return {
      hasLooseItems: false,
      actualDeliveryFee: 0,
      actualHandlingFee: 0,
      originalDeliveryFee: 0,
      originalHandlingFee: 0,
      estimatedTime: 'Today in 1-2 hours',
      deliveryType: DeliveryType.SAME_DAY,
      timeRemaining: getTimeRemainingUntilCutoff(),
      canDeliverToday: true
    };
  }

  // Scenario 2b: No loose items, before 4 PM, cart < 100 - same day with delivery fee
  if (isBeforeCutoff && subtotal < MIN_ORDER_FOR_FREE_DELIVERY) {
    return {
      hasLooseItems: false,
      actualDeliveryFee: DELIVERY_FEE_STANDARD,
      actualHandlingFee: 0,
      originalDeliveryFee: DELIVERY_FEE_STANDARD,
      originalHandlingFee: 0,
      estimatedTime: 'Today in 1-2 hours',
      deliveryType: DeliveryType.SAME_DAY,
      timeRemaining: getTimeRemainingUntilCutoff(),
      canDeliverToday: true
    };
  }

  // Scenario 3: After 4 PM - next day delivery
  const fee = subtotal >= MIN_ORDER_FOR_FREE_DELIVERY ? 0 : DELIVERY_FEE_STANDARD;
  return {
    hasLooseItems: false,
    actualDeliveryFee: fee,
    actualHandlingFee: 0,
    originalDeliveryFee: fee === 0 ? 0 : DELIVERY_FEE_STANDARD,
    originalHandlingFee: 0,
    estimatedTime: getNextDayDeliveryTime(),
    deliveryType: DeliveryType.NEXT_DAY_AFTER_CUTOFF,
    timeRemaining: '',
    canDeliverToday: false
  };
};

/**
 * Get time remaining until cutoff (4 PM)
 * @returns {string} Formatted time remaining
 */
export const getTimeRemainingUntilCutoff = () => {
  const now = new Date();
  const cutoff = new Date();
  cutoff.setHours(CUTOFF_HOUR, 0, 0, 0);

  const diffMillis = cutoff.getTime() - now.getTime();
  if (diffMillis <= 0) return '';

  const hours = Math.floor(diffMillis / (1000 * 60 * 60));
  const minutes = Math.floor((diffMillis / (1000 * 60)) % 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
};

/**
 * Get next day delivery time string
 * @returns {string} Formatted next day delivery string
 */
const getNextDayDeliveryTime = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  const options = { weekday: 'short', day: '2-digit', month: 'short' };
  const formattedDate = tomorrow.toLocaleDateString('en-US', options);
  
  return `Tomorrow (${formattedDate})`;
};

/**
 * Get count of loose items in cart
 * @param {Array} cartItems - Cart items
 * @returns {number} Count of loose items
 */
export const getLooseItemsCount = (cartItems) => {
  return cartItems.filter(item => item.loose_item === true).length;
};

/**
 * Get count of packaged items in cart
 * @param {Array} cartItems - Cart items
 * @returns {number} Count of packaged items
 */
export const getPackagedItemsCount = (cartItems) => {
  return cartItems.filter(item => item.loose_item !== true).length;
};

/**
 * Calculate delivery fee based on subtotal and cart contents
 * @param {number} subtotal - Order subtotal
 * @param {Array} cartItems - Cart items
 * @returns {number} Delivery fee
 */
export const calculateDeliveryFee = (subtotal, cartItems) => {
  const deliveryInfo = calculateDeliveryInfo(cartItems, subtotal);
  return deliveryInfo.actualDeliveryFee;
};

/**
 * Calculate handling charge based on subtotal and cart contents
 * @param {number} subtotal - Order subtotal
 * @param {Array} cartItems - Cart items
 * @returns {number} Handling charge
 */
export const calculateHandlingCharge = (subtotal, cartItems) => {
  const deliveryInfo = calculateDeliveryInfo(cartItems, subtotal);
  return deliveryInfo.actualHandlingFee;
};

export default {
  calculateDeliveryInfo,
  getTimeRemainingUntilCutoff,
  getLooseItemsCount,
  getPackagedItemsCount,
  calculateDeliveryFee,
  calculateHandlingCharge,
  DeliveryType,
  CUTOFF_HOUR,
  MIN_ORDER_FOR_FREE_DELIVERY
};
